import { useCallback, useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { CapabilityCheck } from '@hcs/authz';
import {
  Button,
  Card,
  CardTitle,
  CopyInput,
  LoadingSpinner,
  NoContent,
  StatusMessage,
  Switch,
  TopLevelBanner,
} from '@hcs/design-system';
import { Input, TextArea } from '@hcs/design-system';
import { FormFieldsSwitch, InputLayout, InputLayoutProps } from '@hcs/forms';
import { SamlProviderConfig, SamlProviderConfigFormData } from '@hcs/types';
import { SOLUTIONS_URL } from '@hcs/urls';
import { logException } from '@hcs/utils';

import { useActivateSamlProvider } from '../../hooks/useActivateSamlProvider';
import { useSamlProvider } from '../../hooks/useSamlProvider';
import { useSaveSamlProvider } from '../../hooks/useSaveSamlProvider';
import { SamlIdInput } from '../../types/Saml.types';
import { DeleteSamlProviderDialog } from '../DeleteSamlProviderDialog';

import styles from './SamlProviderForm.module.css';

const INACTIVITY_TIMEOUT_FIELD_LABEL = 'Inactivity Timeout (minutes)';
const SESSION_LENGTH_FIELD_LABEL = 'Session Length (minutes)';

const FORM_SCHEMA = yup.object().shape({
  certificate: yup.string().required('Certificate is required'),
  authMetadata: yup.string().required('Auth Metadata is required'),
  authAudience: yup.string().nullable(),
  domain: yup.string().required('Domain is required'),
  activated: yup.boolean().default(false),
  mandatory: yup.boolean().default(false),
  inactivityTimeoutMinutes: yup
    .number()
    .min(1, `${INACTIVITY_TIMEOUT_FIELD_LABEL} must be at least 1`)
    .nullable(),
  sessionLengthMinutes: yup
    .number()
    .min(1, `${SESSION_LENGTH_FIELD_LABEL} must be at least 1`)
    .nullable(),
});

interface BaseInputProps {
  label: string;
  labelHelper?: InputLayoutProps['labelHelper'];
  required: boolean;
  form: UseFormReturn<SamlProviderConfigFormData>;
  dataHcName: string;
}

interface TextAreaProps extends BaseInputProps {
  fieldName: Extract<
    keyof SamlProviderConfigFormData,
    'certificate' | 'authMetadata'
  >;
}

const TextAreaField = ({
  label,
  required,
  fieldName,
  form,
  dataHcName,
}: TextAreaProps) => {
  return (
    <InputLayout label={label} required={required} className={styles.Field}>
      <Controller
        name={fieldName}
        control={form.control}
        render={({ field }) => (
          <TextArea
            {...field}
            placeholder={label}
            value={field.value || ''}
            dataHcName={`${dataHcName}-input`}
            onChange={field.onChange}
            required={required}
            error={form.formState.errors[fieldName]?.message}
          />
        )}
      />
    </InputLayout>
  );
};

interface TextInputProps extends BaseInputProps {
  readOnly?: boolean;
  fieldName: Extract<
    keyof SamlProviderConfigFormData,
    'authAudience' | 'domain'
  >;
}

const TextField = ({
  label,
  labelHelper,
  required,
  fieldName,
  form,
  dataHcName,
  readOnly,
}: TextInputProps) => {
  return (
    <InputLayout
      label={label}
      labelHelper={labelHelper}
      required={required}
      className={styles.Field}
    >
      <Controller
        name={fieldName}
        control={form.control}
        render={({ field }) => (
          <Input
            {...field}
            readOnly={readOnly}
            placeholder={label}
            value={field.value || ''}
            dataHcName={`${dataHcName}-input`}
            onChange={field.onChange}
            required={required}
            error={form.formState.errors[fieldName]?.message}
          />
        )}
      />
    </InputLayout>
  );
};

interface NumberInputProps extends BaseInputProps {
  readOnly?: boolean;
  fieldName: Extract<
    keyof SamlProviderConfigFormData,
    'inactivityTimeoutMinutes' | 'sessionLengthMinutes'
  >;
}

const NumberField = ({
  label,
  labelHelper,
  required,
  fieldName,
  form,
  dataHcName,
  readOnly,
}: NumberInputProps) => {
  return (
    <InputLayout
      label={label}
      labelHelper={labelHelper}
      required={required}
      className={styles.Field}
    >
      <Controller
        name={fieldName}
        control={form.control}
        render={({ field }) => (
          <Input
            {...field}
            readOnly={readOnly}
            placeholder={label}
            value={field.value?.toString() || ''}
            type="number"
            dataHcName={`${dataHcName}-input`}
            onChange={(val) => {
              field.onChange(val === '' ? null : Number(val));
            }}
            required={required}
            error={form.formState.errors[fieldName]?.message}
          />
        )}
      />
    </InputLayout>
  );
};

interface Props {
  idInput?: SamlIdInput;
  mode: 'internal' | 'external';
}

const dataHcName = 'org-saml-provider';
export const SamlProviderForm = ({ idInput, mode }: Props) => {
  const { data: existingSamlProvider, isLoading: useSamlProviderLoading } =
    useSamlProvider(idInput);
  const { mutate: activateOrgSamlProvider } = useActivateSamlProvider();
  const {
    mutate: saveMutationMutate,
    isPending: saveMutationIsLoading,
    isError: saveMutationIsError,
    error: saveMutationError,
  } = useSaveSamlProvider();

  const form = useForm<SamlProviderConfigFormData>({
    resolver: yupResolver(FORM_SCHEMA),
    mode: 'onBlur',
    defaultValues: {
      certificate: null,
      authMetadata: null,
      authAudience: null,
      domain: null,
      mandatory: false,
      inactivityTimeoutMinutes: null,
      sessionLengthMinutes: null,
    },
  });
  const [deleteDialogActive, setDeleteDialogActive] = useState(false);

  const { reset: formReset } = form;

  const resetToExsitingProvider = useCallback(
    (existingSamlProvider: SamlProviderConfig) => {
      formReset({
        authAudience: existingSamlProvider.authAudience,
        authMetadata: existingSamlProvider.authMetadata,
        certificate: existingSamlProvider.certificate,
        domain: existingSamlProvider.domain,
        mandatory: existingSamlProvider.mandatory,
        inactivityTimeoutMinutes: existingSamlProvider.inactivityTimeoutMinutes,
        sessionLengthMinutes: existingSamlProvider.sessionLengthMinutes,
      });
    },
    [formReset],
  );

  const clearForm = useCallback(() => {
    formReset({
      authAudience: null,
      authMetadata: null,
      certificate: null,
      mandatory: false,
      domain: null,
      inactivityTimeoutMinutes: null,
      sessionLengthMinutes: null,
    });
  }, [formReset]);

  // update default values after saml config load (if they exist)
  useEffect(() => {
    if (existingSamlProvider) {
      resetToExsitingProvider(existingSamlProvider);
    }
  }, [existingSamlProvider, resetToExsitingProvider]);

  if (useSamlProviderLoading) {
    return <LoadingSpinner dataHcName={`${dataHcName}-loader`} />;
  }

  const handleSubmit = (formData: SamlProviderConfigFormData) => {
    if (idInput !== undefined) {
      saveMutationMutate({
        formData,
        idInput,
        existingSamlProvider: existingSamlProvider || null,
      });
    } else {
      logException('OrgSamlProvider: orgId is undefined on submit');
    }
  };

  const showDeleteButton =
    existingSamlProvider !== undefined &&
    existingSamlProvider !== null &&
    idInput !== undefined;

  return (
    <CapabilityCheck
      capabilityKey="org-sso-config"
      error403={
        <NoContent>
          You do not have permission to edit single sign on settings.
        </NoContent>
      }
    >
      <Card dataHcName={`${dataHcName}-card`} maxWidth={900} noGap>
        <CardTitle
          dataHcName={`${dataHcName}-card-title`}
          className={styles.CardTitle}
        >
          <div>SAML Provider Config</div>
          {showDeleteButton ? (
            <Button
              dataHcName={`${dataHcName}-delete-button`}
              label="Delete"
              onClick={() => setDeleteDialogActive(true)}
              highlightRed
            />
          ) : null}
        </CardTitle>
        {saveMutationIsError && (
          <StatusMessage
            dataHcName={`${dataHcName}-error-message`}
            show={saveMutationIsError}
            type="error"
            title={saveMutationError.message}
          />
        )}
        {existingSamlProvider?.activated && (
          <div className={styles.ActiveInfo}>
            <InputLayout
              label="Org Slug"
              labelHelper={{
                dataHcName: 'org-slug',
                helperContent:
                  'Enter this slug when logging in via the Single Sign On flow.',
              }}
              required={false}
              className={styles.Field}
            >
              <b>{existingSamlProvider.organizationSlug}</b>
            </InputLayout>
            <InputLayout
              label="Login Link"
              labelHelper={{
                dataHcName: 'sso-login-link',
                helperContent:
                  'Bookmark this link to be automatically logged in via SSO. Note: this login URL will only work for SP-initiated SAML.',
              }}
              required={false}
              className={styles.Field}
            >
              <CopyInput
                dataHcName="sso-login-link-input"
                value={
                  SOLUTIONS_URL +
                  `/sso/${existingSamlProvider.organizationSlug}`
                }
              />
            </InputLayout>
            <InputLayout
              label="Entity ID"
              required={false}
              className={styles.Field}
            >
              <CopyInput
                dataHcName="entity-id-input"
                value={existingSamlProvider.ssoURL || ''}
              />
            </InputLayout>
            <InputLayout
              label="ACS URL"
              required={false}
              className={styles.Field}
            >
              <CopyInput
                dataHcName="acs-url-input"
                value={existingSamlProvider.ssoURL || ''}
              />
            </InputLayout>
          </div>
        )}
        {existingSamlProvider && !existingSamlProvider.activated && (
          <TopLevelBanner
            dataHcName="saml-activate-banner"
            className={styles.ActiveWarning}
          >
            We are reviewing your SAML configuration and will notify you when it
            has been activated.
          </TopLevelBanner>
        )}
        <FormProvider {...form}>
          <form
            data-hc-name={dataHcName}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {mode === 'internal' && existingSamlProvider && idInput && (
              <InputLayout
                label="Activated"
                required={false}
                className={styles.Field}
              >
                <Switch
                  dataHcName={`${dataHcName}-activated-switch`}
                  switchOffOption={{ value: false }}
                  switchOnOption={{ value: true }}
                  value={!!existingSamlProvider?.activated}
                  onChange={(activated) => {
                    activateOrgSamlProvider({
                      idInput,
                      providerId: existingSamlProvider.id,
                      activated,
                    });
                  }}
                />
              </InputLayout>
            )}
            <TextField
              label="Domain"
              labelHelper={{
                dataHcName: `${dataHcName}-domain-field`,
                helperContent:
                  'Domain should match the email address domain of your users. Example: for user email "bob@company1.com" the domain is "company1.com" (without quotes)',
              }}
              required
              fieldName="domain"
              dataHcName={`${dataHcName}-domain-field`}
              form={form}
              readOnly={!!existingSamlProvider?.activated}
            />
            <TextAreaField
              label="Certificate"
              required
              fieldName="certificate"
              dataHcName={`${dataHcName}-certificate-field`}
              form={form}
            />
            <TextAreaField
              label="Auth Metadata"
              required
              fieldName="authMetadata"
              dataHcName={`${dataHcName}-auth-metadata-field`}
              form={form}
            />
            <TextField
              label="Auth Audience"
              required={false}
              fieldName="authAudience"
              dataHcName={`${dataHcName}-auth-audience-field`}
              form={form}
            />
            <NumberField
              label={SESSION_LENGTH_FIELD_LABEL}
              labelHelper={{
                dataHcName: `${dataHcName}-session-length-field`,
                helperContent:
                  'Length of time after which a user will be required to re-authenticate. Leave blank for default.',
              }}
              required={false}
              fieldName="sessionLengthMinutes"
              dataHcName={`${dataHcName}-session-length-field`}
              form={form}
            />
            <NumberField
              label={INACTIVITY_TIMEOUT_FIELD_LABEL}
              labelHelper={{
                dataHcName: `${dataHcName}-inactivity-timeout-field`,
                helperContent:
                  'Length of time after which a user will be logged out due to inactivity.',
              }}
              required={false}
              fieldName="inactivityTimeoutMinutes"
              dataHcName={`${dataHcName}-inactivity-timeout-field`}
              form={form}
            />
            <InputLayout
              label="Mandatory"
              labelHelper={{
                dataHcName,
                helperContent:
                  'If enabled, all users will be required to use SSO to log in',
              }}
              required={false}
              className={styles.Field}
            >
              <FormFieldsSwitch label="" name="mandatory" />
            </InputLayout>
            <div className={styles.FormButtons}>
              {existingSamlProvider ? (
                <Button
                  dataHcName={`${dataHcName}-cancel-button`}
                  label="Cancel Changes"
                  disabled={!form.formState.isDirty || saveMutationIsLoading}
                  primary={false}
                  onClick={() => resetToExsitingProvider(existingSamlProvider)}
                />
              ) : null}

              <Button
                dataHcName={`${dataHcName}-save-button`}
                type="submit"
                label="Save"
                disabled={saveMutationIsLoading || !form.formState.isValid}
              />
            </div>
          </form>
        </FormProvider>
        {showDeleteButton ? (
          <DeleteSamlProviderDialog
            active={deleteDialogActive}
            onClose={() => setDeleteDialogActive(false)}
            providerId={existingSamlProvider.id}
            idInput={idInput}
            onDeleteSuccess={clearForm}
          />
        ) : null}
      </Card>
    </CapabilityCheck>
  );
};
